import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { bagsApi } from '../../services/api';
import { Input } from '../../components/ui/input';
import ImageUpload from '../../components/common/ImageUpload';
import { toast } from 'sonner';

const AdminAddBag = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    bagAd: '',
    bagNov: '',
    bagPrice: '',
    bagCategory: '',
    bagImg: '',
    images: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMainImageChange = (imageData) => {
    setFormData((prev) => ({ ...prev, bagImg: imageData }));
  };

  const handleGalleryChange = (imagesData) => {
    setFormData((prev) => ({ ...prev, images: imagesData }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.bagAd || !formData.bagPrice) {
      toast.error('Ad və qiymət sahələri mütləqdir');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        bagAd: formData.bagAd,
        bagNov: formData.bagNov,
        bagPrice: parseFloat(formData.bagPrice) || 0,
        bagCategory: formData.bagCategory,
        bagImg: formData.bagImg,
        images: formData.images.map((img) => ({ img })),
      };

      await bagsApi.create(payload);
      toast.success('Çanta uğurla əlavə edildi!');
      navigate('/admin/bags');
    } catch (err) {
      console.error('Error adding bag:', err);
      toast.error('Çanta əlavə etmək mümkün olmadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-add-bag">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/bags')}
          className="inline-flex items-center gap-2 text-brand-muted hover:text-brand-primary transition-colors mb-4"
          data-testid="back-to-bags"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Çantalara qayıt</span>
        </button>
        <h1 className="font-serif text-2xl text-brand-brown">Yeni çanta əlavə et</h1>
        <p className="text-sm text-brand-muted mt-1">Çanta məlumatlarını daxil edin</p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl p-8 shadow-card">
        <form onSubmit={handleSubmit} className="space-y-8" data-testid="add-bag-form">
          {/* Basic Info */}
          <div>
            <h3 className="font-serif text-lg text-brand-brown mb-6 pb-3 border-b border-brand-secondary/30">
              Əsas məlumatlar
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-brand-brown mb-2">
                  Çanta adı <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="bagAd"
                  value={formData.bagAd}
                  onChange={handleChange}
                  required
                  placeholder="Məs: Toxuma əl çantası"
                  className="rounded-xl border-brand-secondary focus:border-brand-primary"
                  data-testid="input-bagAd"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-2">
                  Model / Növ
                </label>
                <Input
                  type="text"
                  name="bagNov"
                  value={formData.bagNov}
                  onChange={handleChange}
                  placeholder="Məs: Çiyindən asılan"
                  className="rounded-xl border-brand-secondary focus:border-brand-primary"
                  data-testid="input-bagNov"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-2">
                  Qiymət (₼) <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  name="bagPrice"
                  value={formData.bagPrice}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="rounded-xl border-brand-secondary focus:border-brand-primary"
                  data-testid="input-bagPrice"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-2">
                  Kateqoriya
                </label>
                <Input
                  type="text"
                  name="bagCategory"
                  value={formData.bagCategory}
                  onChange={handleChange}
                  placeholder="Məs: Əl işi çanta"
                  className="rounded-xl border-brand-secondary focus:border-brand-primary"
                  data-testid="input-bagCategory"
                />
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <h3 className="font-serif text-lg text-brand-brown mb-6 pb-3 border-b border-brand-secondary/30">
              Şəkillər
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Main Image */}
              <ImageUpload
                value={formData.bagImg}
                onChange={handleMainImageChange}
                label="Əsas şəkil"
                className="flex-1"
              />

              {/* Gallery Images */}
              <ImageUpload
                value={formData.images}
                onChange={handleGalleryChange}
                multiple={true}
                label="Qalereya şəkilləri (bir neçə seçə bilərsiniz)"
                className="flex-1"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex items-center gap-4 pt-6 border-t border-brand-secondary/30">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-primary to-[#E08E73] text-white px-8 py-3 font-medium hover:shadow-warm transition-all duration-300 disabled:opacity-50"
              data-testid="submit-bag"
            >
              {loading ? (
                'Əlavə edilir...'
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Yadda saxla
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/bags')}
              className="px-8 py-3 text-brand-muted hover:text-brand-primary transition-colors"
              data-testid="cancel-add-bag"
            >
              Ləğv et
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAddBag;
